import { menuCoachmarkStepAtom } from '@/atoms/menuCoachmarkAtoms';
import { useMutateMarkModalAsViewed } from '@/hooks/mutation/useMutateMarkModalAsViewed';
import { isMac } from '@/hooks/platform';
import { Button } from '@heidi/design-system/button';
import {
  Spotlight,
  SpotlightAnchor,
  SpotlightArrow,
  SpotlightClose,
  SpotlightContent,
  SpotlightDescription,
  SpotlightFooter,
  SpotlightHeader,
  SpotlightProgress,
  SpotlightTitle,
} from '@heidi/design-system/spotlight';
import { useTranslations } from '@heidi/runtime/i18n';
import { ModalId } from '@heidi/schema/ml-scribe';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { PANEL_EXPAND_SETTLE_MS, TOTAL_STEPS } from './constants';

interface MenuCoachmarkPinPersistStepProps {
  children?: ReactNode;
}

/**
 * Final step of the menu coachmark tour.
 * Self-contained: reads atom state directly, provides its own handlers.
 * Shows how to pin the panel to keep sessions visible, including the keyboard shortcut.
 *
 * Always the last step in the tour (step 4 in atom, displayed as totalSteps).
 * The previous step re-pins the panel before transitioning here, so the
 * anchor (pin button in the panel header) is already visible on mount.
 */
export const MenuCoachmarkPinPersistStep = ({
  children,
}: MenuCoachmarkPinPersistStepProps) => {
  const { t } = useTranslations();
  const currentStep = useAtomValue(menuCoachmarkStepAtom);
  const setStep = useSetAtom(menuCoachmarkStepAtom);
  const { mutate: markModalAsViewed } = useMutateMarkModalAsViewed();

  const isStep4 = currentStep === 4;

  const [visibleOpen, setVisibleOpen] = useState(false);

  useEffect(() => {
    if (!isStep4) {
      setVisibleOpen(false);
      return;
    }
    const timeoutId = window.setTimeout(
      () => setVisibleOpen(true),
      PANEL_EXPAND_SETTLE_MS
    );
    return () => window.clearTimeout(timeoutId);
  }, [isStep4]);

  const handleDismiss = useCallback(() => {
    markModalAsViewed({
      modal_id: ModalId.MENU_COACHMARK_TOUR,
      increment_count: true,
    });
  }, [markModalAsViewed]);

  const handleFinish = useCallback(() => {
    handleDismiss();
    setVisibleOpen(false);
    setStep(null);
  }, [handleDismiss, setStep]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setVisibleOpen(false);
        setStep(null);
      }
    },
    [setStep]
  );

  if (!isStep4) return <>{children}</>;

  const hotkey = isMac ? '⌘/' : 'Ctrl+/';

  return (
    <Spotlight open={visibleOpen} onOpenChange={handleOpenChange}>
      {children && <SpotlightAnchor>{children}</SpotlightAnchor>}
      <SpotlightContent
        align="start"
        alignOffset={0}
        side="bottom"
        sideOffset={8}
        data-testid="menu-coachmark-step-4"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <SpotlightArrow width={12} height={8} />
        <SpotlightHeader>
          <SpotlightTitle>{t('menuCoachmark.pinPersistStepTitle')}</SpotlightTitle>
          <SpotlightClose
            onClick={handleDismiss}
            data-testid="menu-coachmark-step-4-close"
          />
        </SpotlightHeader>
        <SpotlightDescription>
          {t('menuCoachmark.pinPersistStepDescription', { hotkey })}
        </SpotlightDescription>
        <SpotlightFooter>
          <SpotlightProgress>
            {t('menuCoachmark.stepProgress', {
              current: TOTAL_STEPS,
              total: TOTAL_STEPS,
            })}
          </SpotlightProgress>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleFinish}
            className="bg-surface text-text-primary hover:bg-background-secondary"
            data-testid="menu-coachmark-step-4-finish"
          >
            {t('menuCoachmark.finish')}
          </Button>
        </SpotlightFooter>
      </SpotlightContent>
    </Spotlight>
  );
};
